<template>
  <div class="production-order-page">
    <KTCodePreview title="Lệnh sản xuất">
      <template v-slot:title>
        <span class="po-title">
          <i class="mdi mdi-creation po-title-icon"></i> Lệnh sản xuất
        </span>
      </template>
      <template v-slot:toolbar>
        <span class="production-order-api-hint d-none d-lg-inline">POST /product-imei-stocks/change-*</span>
      </template>
      <template v-slot:preview>
        <p class="production-order-subtitle">
          Tạo phiếu đổi sản phẩm / đổi IMEI giữa các hàng tồn kho — phát sinh phiếu Nhập kho &amp; Xuất kho tự động.
        </p>

        <div class="po-meta-card mb-3">
          <div class="po-meta-field po-meta-field--store">
            <span class="po-meta-label">
              <i class="mdi mdi-warehouse"></i> Kho <span class="text-danger">*</span>
            </span>
            <vue-autosuggest
              class="po-meta-suggest"
              :suggestions="filteredStoreOptions"
              :limit="200"
              :input-props="storeInputProps"
              :should-render-suggestions="shouldRenderStoreSuggestions"
              v-model="searchStore"
              @selected="onSelectedStore"
              @input="onStoreInput"
            >
              <div slot-scope="{ suggestion }">
                {{ suggestion.item.suggestionName }}
              </div>
            </vue-autosuggest>
          </div>
          <div class="po-meta-divider"></div>
          <div class="po-meta-field po-meta-field--note">
            <span class="po-meta-label">Ghi chú</span>
            <b-form-input
              class="po-meta-input"
              v-model="description"
              placeholder="VD: Đổi vỏ máy theo yêu cầu khách hàng — phiếu #1234"
            />
          </div>
        </div>

        <div class="po-tab-group mb-3">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="po-tab"
            :class="{
              'po-tab--active': activeTab === tab.id,
              'po-tab--disabled': tab.disabled,
            }"
            :disabled="tab.disabled"
            @click="onTabClick(tab)"
          >
            <i class="mdi po-tab-icon" :class="tab.icon"></i>
            <span class="po-tab-label">{{ tab.label }}</span>
            <span class="po-tab-sub">{{ tab.subLabel }}</span>
          </button>
        </div>

        <ProductionOrderSearchBar
          :search-type.sync="searchType"
          :search-text="searchText"
          :suggestions="searchSuggestions"
          :disabled="!storeId"
          :show-hint="activeTab === 'change-product'"
          :show-price="canViewOriginalPrice"
          @search-input="onSearchInput"
          @selected="onSearchSelected"
        />

        <ProductionOrderExchangeTable
          :rows="currentRows"
          :mode="activeTab"
          :product-new-suggestions="productNewSuggestions"
          :show-price="canViewOriginalPrice"
          @product-new-search="onProductNewSearch"
          @product-new-selected="onProductNewSelected"
          @product-new-focus="onProductNewFocus"
          @imei-new-input="onImeiNewInput"
          @remove-row="removeRow"
        />

        <div class="po-footer-bar d-flex justify-content-between align-items-center">
          <span class="po-footer-summary">
            <i class="mdi mdi-check-circle"></i> {{ footerSummary }}
          </span>
          <div class="po-footer-actions">
            <router-link to="/imeis" tag="button">
              <b-button variant="light" size="sm" class="mr-2" :disabled="submitting">
                <i class="mdi mdi-close"></i> Hủy
              </b-button>
            </router-link>
            <b-button
              size="sm"
              class="po-save-btn"
              :disabled="!canSubmit"
              @click="onSubmit"
              v-if="checkPermission('PRODUCTION_ORDER_CREATE')"
            >
              <b-spinner v-if="submitting" small class="mr-1" />
              <i v-else class="mdi mdi-content-save"></i>
              Lưu phiếu
            </b-button>
          </div>
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import debounce from 'debounce';
import { v4 } from 'uuid';
import {
  unMaskPrice,
  convertPrice,
} from '@/utils/common';
import permissionMixin from '@/view/mixins/permission.mixin';
import {
  isProductSearchSkipped,
  PRODUCT_SEARCH_DEBOUNCE_MS,
  searchForChange,
  searchImeiForChange,
  unwrapProductSearchData,
} from '@/utils/productSearch';
import ProductionOrderSearchBar from './components/ProductionOrderSearchBar.vue';
import ProductionOrderExchangeTable from './components/ProductionOrderExchangeTable.vue';

const TAB_CHANGE_PRODUCT = 'change-product';
const TAB_CHANGE_IMEI = 'change-imei';

function createEmptyRow() {
  return {
    id: v4(),
    productId: '',
    productName: '',
    productCode: '',
    barCode: '',
    productImageUrl: '',
    imeiOld: '',
    exportPriceDisplay: '',
    importPriceDisplay: '',
    productIdNew: null,
    productNameNew: '',
    productCodeNew: '',
    barCodeNew: '',
    productImageUrlNew: '',
    imeiNew: '',
    fieldErrors: { productIdNew: '', imeiNew: '' },
  };
}

export default {
  name: 'ProductionOrder',
  mixins: [permissionMixin],
  components: {
    KTCodePreview,
    ProductionOrderSearchBar,
    ProductionOrderExchangeTable,
  },
  data() {
    return {
      tabs: [
        { id: TAB_CHANGE_PRODUCT, label: 'Đổi sản phẩm', subLabel: '1 IMEI ↔ SP khác', icon: 'mdi-package-variant-closed', disabled: false },
        { id: TAB_CHANGE_IMEI, label: 'Đổi IMEI', subLabel: 'Giữ SP, thay IMEI', icon: 'mdi-cellphone', disabled: false },
        { id: 'disassembly', label: 'Rã xác', subLabel: 'Sắp ra mắt', icon: 'mdi-hexagon-multiple-outline', disabled: true },
      ],
      activeTab: TAB_CHANGE_PRODUCT,
      storeId: null,
      storeShortName: '',
      searchStore: '',
      storeOptions: [{ data: [] }],
      filteredStoreOptions: [{ data: [] }],
      storeInputProps: {
        class: 'autosuggest__input',
        placeholder: 'Chọn kho',
      },
      description: '',
      searchType: 2,
      searchText: '',
      searchSuggestions: [{ data: [] }],
      productNewSuggestions: [{ data: [] }],
      productNewSearchText: '',
      productNewFocusIndex: null,
      rowsChangeProduct: [],
      rowsChangeImei: [],
      submitting: false,
    };
  },
  computed: {
    currentRows() {
      return this.activeTab === TAB_CHANGE_IMEI ? this.rowsChangeImei : this.rowsChangeProduct;
    },
    canViewOriginalPrice() {
      return this.checkPermission('PRODUCT_ORIGINAL_PRICE_VIEW');
    },
    footerSummary() {
      const count = this.currentRows.length;
      const storeLabel = this.storeShortName ? `Kho ${this.storeShortName}` : 'Chưa chọn kho';
      return `${count} dòng · ${storeLabel}`;
    },
    hasValidationErrors() {
      return this.currentRows.some(row => row.fieldErrors.imeiNew || row.fieldErrors.productIdNew);
    },
    canSubmit() {
      return (
        !this.submitting
        && this.storeId
        && this.currentRows.length > 0
        && !this.hasValidationErrors
        && this.currentRows.every(row => this.isRowComplete(row))
      );
    },
  },
  watch: {
    '$route.query.tab': {
      immediate: true,
      handler(tab) {
        if (tab === TAB_CHANGE_IMEI) {
          this.activeTab = TAB_CHANGE_IMEI;
        } else if (tab === TAB_CHANGE_PRODUCT || !tab) {
          this.activeTab = TAB_CHANGE_PRODUCT;
        }
      },
    },
  },
  created() {
    this.fetchStores();
    this.debouncedSearch = debounce(this.runSearch, PRODUCT_SEARCH_DEBOUNCE_MS);
    this.debouncedProductNewSearch = debounce(this.runProductNewSearch, PRODUCT_SEARCH_DEBOUNCE_MS);
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Danh sách IMEI', route: '/imeis' },
      { title: 'Lệnh sản xuất' },
    ]);

    const imeiId = this.$route.query.imeiId;
    if (imeiId) {
      this.prefillFromImeiId(imeiId);
    }
  },
  methods: {
    shouldRenderStoreSuggestions(size, loading) {
      return (
        size >= 0
        && !loading
        && !this.storeId
        && Boolean((this.searchStore || '').trim())
      );
    },
    isRowComplete(row) {
      if (!row.productId || !row.imeiOld) return false;
      if (this.activeTab === TAB_CHANGE_PRODUCT) {
        return !!row.productIdNew && row.productIdNew !== row.productId;
      }
      return !!row.imeiNew && row.imeiNew.trim() !== row.imeiOld.trim();
    },
    onTabClick(tab) {
      if (tab.disabled) return;
      this.activeTab = tab.id;
      this.searchText = '';
      this.searchSuggestions = [{ data: [] }];
      this.$router.replace({ query: { ...this.$route.query, tab: tab.id } }).catch(() => {});
    },
    async fetchStores() {
      const { data } = await ApiService.get('stores?limit=10000&page=1');
      const stores = (data.data.list_store || []).map(element => ({
        id: element.id,
        name: element.name,
        shortName: element.shortName,
        suggestionName: `${element.shortName} (${element.name})`,
      }));
      this.storeOptions = [{ data: stores }];
      this.filteredStoreOptions = [{ data: stores }];
    },
    onStoreInput(text) {
      this.searchStore = text;
      if (!text) {
        this.storeId = null;
        this.storeShortName = '';
      }
      const filtered = this.storeOptions[0].data.filter(item =>
        item.name.toLowerCase().includes(text.toLowerCase())
        || item.shortName.toLowerCase().includes(text.toLowerCase())
      );
      this.filteredStoreOptions = [{ data: filtered }];
    },
    async onSelectedStore(option) {
      const newStoreId = option.item.id;
      if (this.storeId && this.storeId !== newStoreId && this.currentRows.length) {
        const ok = await this.$bvModal.msgBoxConfirm(
          'Đổi kho sẽ xóa danh sách hiện tại. Tiếp tục?',
          { title: 'Xác nhận', okVariant: 'danger' }
        );
        if (!ok) return;
        this.rowsChangeProduct = [];
        this.rowsChangeImei = [];
      }
      this.searchStore = option.item.name;
      this.storeId = newStoreId;
      this.storeShortName = option.item.shortName;
    },
    onSearchInput(text) {
      this.searchText = text || '';
      this.debouncedSearch();
    },
    async runSearch() {
      if (!this.storeId) return;
      const text = (this.searchText || '').trim();
      if (!text || isProductSearchSkipped(text)) {
        this.searchSuggestions = [{ data: [] }];
        return;
      }
      const params = { storeId: this.storeId };
      const response = this.searchType === 1
        ? await searchForChange({ ...params, searchProduct: text })
        : await searchImeiForChange({ ...params, imeiCode: text });
      const products = unwrapProductSearchData(response);
      this.searchSuggestions = [{ data: products }];
    },
    onSearchSelected(item) {
      const row = createEmptyRow();
      row.productId = item.productId;
      row.productName = item.productName;
      row.productCode = item.productCode;
      row.barCode = item.barCode;
      row.productImageUrl = item.productImageUrl;
      row.imeiOld = item.imeiCode || '';
      const priceDisplay = item.originalPrice != null ? convertPrice(item.originalPrice) : '';
      row.exportPriceDisplay = priceDisplay;
      row.importPriceDisplay = priceDisplay;

      if (this.activeTab === TAB_CHANGE_IMEI) {
        this.rowsChangeImei.push(row);
      } else {
        this.rowsChangeProduct.push(row);
      }
      this.searchText = '';
      this.searchSuggestions = [{ data: [] }];
    },
    onProductNewFocus(index) {
      this.productNewFocusIndex = index;
    },
    onProductNewSearch(text, index) {
      this.productNewFocusIndex = index;
      this.productNewSearchText = text || '';
      const row = this.rowsChangeProduct[index];
      if (row) row.productNameNew = text || '';
      this.debouncedProductNewSearch();
    },
    async runProductNewSearch() {
      const text = (this.productNewSearchText || '').trim();
      if (!this.storeId || !text || isProductSearchSkipped(text)) {
        this.productNewSuggestions = [{ data: [] }];
        return;
      }
      const response = await searchForChange({
        storeId: this.storeId,
        searchProduct: text,
      });
      this.productNewSuggestions = [{ data: unwrapProductSearchData(response) }];
    },
    onProductNewSelected(item, index) {
      const rows = this.rowsChangeProduct;
      const row = rows[index !== undefined ? index : this.productNewFocusIndex];
      if (!row) return;
      if (item.productId === row.productId) {
        row.fieldErrors.productIdNew = 'Sản phẩm nhập phải khác sản phẩm xuất';
        return;
      }
      row.productIdNew = item.productId;
      row.productNameNew = item.productName;
      row.productCodeNew = item.productCode;
      row.barCodeNew = item.barCode;
      row.productImageUrlNew = item.productImageUrl;
      row.fieldErrors.productIdNew = '';
      if (!row.importPriceDisplay && item.originalPrice != null) {
        row.importPriceDisplay = convertPrice(item.originalPrice);
      }
    },
    onImeiNewInput(value, index) {
      const row = this.rowsChangeImei[index];
      if (!row) return;
      row.imeiNew = value;
      const trimmed = (value || '').trim();
      if (!trimmed) {
        row.fieldErrors.imeiNew = '';
      } else if (trimmed === row.imeiOld) {
        row.fieldErrors.imeiNew = 'Trùng IMEI cũ';
      } else if (this.rowsChangeImei.some((r, i) => i !== index && r.imeiNew.trim() === trimmed)) {
        row.fieldErrors.imeiNew = 'Trùng IMEI trong phiếu';
      } else {
        row.fieldErrors.imeiNew = '';
      }
    },
    removeRow(rowId) {
      if (this.activeTab === TAB_CHANGE_IMEI) {
        this.rowsChangeImei = this.rowsChangeImei.filter(r => r.id !== rowId);
      } else {
        this.rowsChangeProduct = this.rowsChangeProduct.filter(r => r.id !== rowId);
      }
    },
    async prefillFromImeiId(imeiId) {
      try {
        const { data } = await ApiService.post('v1/product-imei-stocks/detail-by-ids', {
          ids: [Number(imeiId)],
        });
        const detail = (data.data || [])[0];
        if (!detail) return;

        const legacy = await ApiService.query(`productImeiStock/getProductImei/${imeiId}`);
        const resp = legacy?.data?.data || {};

        if (resp.storeId) {
          this.storeId = resp.storeId;
          this.searchStore = resp.storeName || '';
          this.storeShortName = resp.storeShortName || '';
        }

        const row = createEmptyRow();
        row.productId = detail.productId || resp.productId;
        row.productName = detail.productName || resp.productName;
        row.productCode = detail.productCode || resp.productCode;
        row.barCode = detail.barCode || resp.barCode;
        row.productImageUrl = resp.productImageUrl || '';
        row.imeiOld = detail.imeiNo || resp.imeiNo;
        if (detail.originalPrice != null || resp.originalPrice != null) {
          const price = detail.originalPrice != null ? detail.originalPrice : resp.originalPrice;
          const priceDisplay = convertPrice(price);
          row.exportPriceDisplay = priceDisplay;
          row.importPriceDisplay = priceDisplay;
        }

        if (this.activeTab === TAB_CHANGE_IMEI) {
          this.rowsChangeImei.push(row);
        } else {
          this.rowsChangeProduct.push(row);
        }
      } catch (e) {
        // ignore prefill errors
      }
    },
    buildChangeProductPayload() {
      return {
        storeId: this.storeId,
        description: this.description,
        listProduct: this.rowsChangeProduct.map(row => ({
          productId: row.productId,
          productIdNew: row.productIdNew,
          imeis: row.imeiOld,
          quantity: 1,
          originalPrice: unMaskPrice(row.importPriceDisplay || row.exportPriceDisplay) || 0,
        })),
      };
    },
    buildChangeImeiPayload() {
      return {
        storeId: this.storeId,
        description: this.description,
        listProduct: this.rowsChangeImei.map(row => ({
          productId: row.productId,
          imeiOld: row.imeiOld,
          imeiNew: row.imeiNew.trim(),
          quantity: 1,
          originalPrice: unMaskPrice(row.exportPriceDisplay) || 0,
        })),
      };
    },
    async onSubmit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      const endpoint = this.activeTab === TAB_CHANGE_IMEI
        ? 'v1/product-imei-stocks/change-imei'
        : 'v1/product-imei-stocks/change-product-imei';
      const payload = this.activeTab === TAB_CHANGE_IMEI
        ? this.buildChangeImeiPayload()
        : this.buildChangeProductPayload();

      try {
        const { data } = await ApiService.post(endpoint, payload);
        let message = data.message || 'Lưu thành công';
        if (data.data?.importCode && data.data?.exportCode) {
          message += ` (${data.data.importCode} / ${data.data.exportCode})`;
        }
        this.$bvToast.toast(message, { title: 'Thông báo', variant: 'success', solid: true });
        setTimeout(() => {
          this.$router.push({ name: 'production-order-list' });
        }, 1200);
      } catch (error) {
        const message = error?.response?.data?.message || 'Có lỗi xảy ra, vui lòng thử lại';
        this.$bvToast.toast(message, { title: 'Thông báo', variant: 'danger', solid: true });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import './production-order.scss';
</style>
